import { useState, useEffect } from 'react';
import { getFeatureFlag, onFeatureFlags } from '../services/posthog';

/**
 * useFeatureFlag — read a PostHog feature flag reactively for A/B testing.
 *
 * Returns `fallback` until PostHog finishes loading flags, then the flag's value,
 * re-rendering if the value changes. Flag value is a boolean (boolean flags) or a
 * string (multivariate flags), so callers compare against the expected variant.
 *
 * @param {string} key - feature flag key configured in PostHog
 * @param {*} fallback - value returned before flags load / when unset (default false)
 *
 * @example
 *   const variant = useFeatureFlag('cc_wall_copy', 'control');
 *   if (variant === 'trust_signals') { ... }
 */
export const useFeatureFlag = (key, fallback = false) => {
  const [value, setValue] = useState(() => getFeatureFlag(key, fallback));

  useEffect(() => {
    // Resolve immediately (flags may already be loaded) and on every flag update.
    setValue(getFeatureFlag(key, fallback));
    const unsubscribe = onFeatureFlags(() => setValue(getFeatureFlag(key, fallback)));
    return unsubscribe;
  }, [key, fallback]);

  return value;
};

export default useFeatureFlag;
